import React, { useRef, useEffect, useMemo } from 'react'
import { Layer, Line, Image as KonvaImage } from 'react-konva'
import Konva from 'konva'

import { Line as LineInterface } from '../../../reducers/editPhoto'
import { getSquareLinePoints, getTriangleLinePoints } from '../../../utils/geometryCalculations'

interface AIEraserLayerProps {
    aiEraserLines: LineInterface[]
    tempAIEraserLines: LineInterface[]
    width: number
    height: number
}

/**
 * □/△の線をオフスクリーンCanvasに不透明な赤色で描画する
 * KonvaImage側でopacityを適用することで、ポリゴン重なり部分のアルファ重畳を防止する
 */
function drawPolygonLinesToCanvas(
    lines: LineInterface[],
    width: number,
    height: number
): HTMLCanvasElement | null {
    const polygonLines = lines.filter(l => l.head === 'square' || l.head === 'triangle')
    if (width === 0 || height === 0 || polygonLines.length === 0) return null

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.fillStyle = 'rgb(255, 0, 0)'

    polygonLines.forEach(line => {
        if (line.points.length < 4) return

        if (line.head === 'square') {
            for (let i = 0; i < line.points.length - 2; i += 2) {
                const x1 = line.points[i], y1 = line.points[i + 1]
                const x2 = line.points[i + 2], y2 = line.points[i + 3]
                if (x2 === undefined) break
                const pts = getSquareLinePoints(x1, y1, x2, y2, line.headSize)
                ctx.beginPath()
                ctx.moveTo(pts[0], pts[1])
                for (let j = 2; j < pts.length; j += 2) ctx.lineTo(pts[j], pts[j + 1])
                ctx.closePath()
                ctx.fill()
            }
        } else if (line.head === 'triangle') {
            for (let i = 0; i < line.points.length - 2; i += 2) {
                const x1 = line.points[i], y1 = line.points[i + 1]
                const x2 = line.points[i + 2], y2 = line.points[i + 3]
                if (x2 === undefined) break
                const pts = getTriangleLinePoints(x1, y1, x2, y2, line.headSize)
                ctx.beginPath()
                ctx.moveTo(pts[0], pts[1])
                for (let j = 2; j < pts.length; j += 2) ctx.lineTo(pts[j], pts[j + 1])
                ctx.closePath()
                ctx.fill()
            }
        }
    })

    return canvas
}

/**
 * AI消しゴム専用レイヤー
 * 確定した線（不透明赤色）と一時的な線（透明赤色）を描画
 * このレイヤーは消しゴムとして動作しない（listening={false}）
 *
 * □/△マーカーはオフスクリーンCanvas方式で描画し、
 * ポリゴン重なり部分のアルファ重畳を防止する
 */
export const AIEraserLayer: React.FC<AIEraserLayerProps> = ({ aiEraserLines, tempAIEraserLines, width, height }) => {
    const layerRef = useRef<Konva.Layer>(null)

    // aiEraserLines と tempAIEraserLines が両方空になった時、レイヤーをクリアして再描画
    useEffect(() => {
        if (aiEraserLines.length === 0 && tempAIEraserLines.length === 0 && layerRef.current) {
            layerRef.current.clear()
            layerRef.current.batchDraw()
        }
    }, [aiEraserLines, tempAIEraserLines])

    // □/△のオフスクリーンCanvas（確定済み）
    const confirmedPolygonCanvas = useMemo(
        () => drawPolygonLinesToCanvas(aiEraserLines, width, height),
        [aiEraserLines, width, height]
    )

    // □/△のオフスクリーンCanvas（描画中）
    const tempPolygonCanvas = useMemo(
        () => drawPolygonLinesToCanvas(tempAIEraserLines, width, height),
        [tempAIEraserLines, width, height]
    )

    return (
        <Layer ref={layerRef} listening={false}>
            {/* ○マーカー（確定済み）- 単一パスなのでアルファ重畳なし */}
            {aiEraserLines
                .filter((line: LineInterface) => line.head === 'circle')
                .map((line: LineInterface, i: number) => (
                    <Line
                        key={`ai-confirmed-circle-${i}`}
                        points={line.points}
                        stroke="rgba(255, 0, 0, 0.6)"
                        strokeWidth={line.headSize}
                        tension={0.3}
                        lineCap="round"
                        lineJoin="round"
                    />
                ))}

            {/* □/△マーカー（確定済み）- オフスクリーンCanvasで均一なopacityを実現 */}
            {confirmedPolygonCanvas && (
                <KonvaImage
                    image={confirmedPolygonCanvas}
                    width={width}
                    height={height}
                    opacity={0.6}
                />
            )}

            {/* ○マーカー（描画中） */}
            {tempAIEraserLines
                .filter((line: LineInterface) => line.head === 'circle')
                .map((line: LineInterface, i: number) => (
                    <Line
                        key={`ai-temp-circle-${i}`}
                        points={line.points}
                        stroke="rgba(255, 0, 0, 0.3)"
                        strokeWidth={line.headSize}
                        tension={0.3}
                        lineCap="round"
                        lineJoin="round"
                    />
                ))}

            {/* □/△マーカー（描画中） */}
            {tempPolygonCanvas && (
                <KonvaImage
                    image={tempPolygonCanvas}
                    width={width}
                    height={height}
                    opacity={0.3}
                />
            )}
        </Layer>
    )
}
